import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api';
import { ErrorBox, Empty, Loading, fmtDate } from '../components/ui';

interface ImportRow {
  id: string;
  status: string;
  filename: string;
  totalRows: number | null;
  added: number | null;
  upgradedFromWeb?: number | null;
  skippedExisting: number | null;
  skippedDuplicate: number | null;
  invalid: number | null;
  createdAt: string;
  committedAt: string | null;
  createdByName?: string;
}

export function ImportHistoryPage() {
  const q = useQuery({ queryKey: ['imports'], queryFn: () => api<{ imports: ImportRow[] }>('/product-imports').then((r) => r.imports) });
  return (
    <div>
      <div className="row between">
        <h1>Product imports</h1>
        <Link className="btn btn-primary" to="/imports/new">
          Import products
        </Link>
      </div>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} onRetry={() => q.refetch()} />
      {q.data?.length === 0 && <Empty>No imports yet.</Empty>}
      {!!q.data?.length && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>File</th>
                <th>Status</th>
                <th>Rows</th>
                <th>Added</th>
                <th>Web items filled</th>
                <th>Existing</th>
                <th>Repeated</th>
                <th>Invalid</th>
                <th>By</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((i) => (
                <tr key={i.id}>
                  <td>
                    <Link to={`/imports/${i.id}`}>{i.id}</Link>
                  </td>
                  <td>{i.filename}</td>
                  <td>{i.status === 'uploaded' ? 'not committed' : i.status}</td>
                  <td>{i.totalRows ?? ''}</td>
                  <td>{i.added ?? ''}</td>
                  <td>{i.upgradedFromWeb ?? ''}</td>
                  <td>{i.skippedExisting ?? ''}</td>
                  <td>{i.skippedDuplicate ?? ''}</td>
                  <td>{i.invalid ?? ''}</td>
                  <td>{i.createdByName}</td>
                  <td className="small">{fmtDate(i.committedAt ?? i.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ImportDetailPage() {
  const { id } = useParams();
  const [filter, setFilter] = useState('problems');
  const q = useQuery({
    queryKey: ['import', id, filter],
    queryFn: () =>
      api<{ import: ImportRow; rows: { sheet: string; rowNumber: number; outcome: string; itemNumber: string | null; messages: string[]; productId: string | null }[] }>(
        `/product-imports/${id}?outcome=${filter}`,
      ),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const { import: imp, rows: shown } = q.data!;
  return (
    <div>
      <h1>
        Import #{imp.id}: {imp.filename}
      </h1>
      <p>
        Status: <strong>{imp.status === 'uploaded' ? 'not committed' : imp.status}</strong>
        {imp.committedAt && ` on ${fmtDate(imp.committedAt)}`}. {imp.added ?? 0} added, {imp.upgradedFromWeb ? `${imp.upgradedFromWeb} web-only items filled in, ` : ''}
        {imp.skippedExisting ?? 0} existing, {imp.skippedDuplicate ?? 0} repeated,{' '}
        {imp.invalid ?? 0} invalid.
      </p>
      {imp.status === 'committed' && (
        <>
          <div className="row gap">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Show rows">
              <option value="problems">Rows not added</option>
              <option value="notes">Rows with notes</option>
              <option value="added">Added</option>
              <option value="all">All rows</option>
            </select>
            <a className="btn btn-small" href={`/api/product-imports/${imp.id}/report.csv`}>
              Download error report
            </a>
            <a className="btn btn-small" href={`/api/product-imports/${imp.id}/report.csv?all=1`}>
              Download all rows
            </a>
          </div>
          <div className="table-wrap">
            <table className="table small">
              <thead>
                <tr>
                  <th>File row</th>
                  <th>Outcome</th>
                  <th>Item #</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={`${r.sheet}-${r.rowNumber}`} className={`outcome-${r.outcome}`}>
                    <td className="nowrap">{r.sheet ? `${r.sheet} ${r.rowNumber}` : r.rowNumber}</td>
                    <td>{r.outcome.replace(/_/g, ' ')}</td>
                    <td className="mono">{r.productId ? <Link to={`/products/${r.productId}`}>{r.itemNumber}</Link> : (r.itemNumber ?? '')}</td>
                    <td className="small">{r.messages.join('; ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {shown.length === 2000 && <p className="muted small">Showing the first 2,000 rows. Download the report for every row.</p>}
        </>
      )}
    </div>
  );
}
